import { useInfiniteQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { campaignIntegrationQueryKeys } from "../api/query-keys";

import { instance } from "@/lib/axios/instance";

const PAGE_SIZE = 50;

type EnrichItemsFn = (items: unknown[]) => Record<string, unknown>[];

const enrichmentRegistry = new Map<string, EnrichItemsFn>();

export function registerCampaignEnrichment(
  integrationName: string,
  fn: EnrichItemsFn,
): void {
  enrichmentRegistry.set(integrationName.toLowerCase(), fn);
}

interface CampaignIntegrationsPage {
  summary: Record<string, unknown>;
  items: Record<string, unknown>[];
  totalRecords: number;
  page: number;
  pageSize: number;
}

interface UseCampaignIntegrationsInfiniteParams {
  integrationName: string;
  clientId: string | undefined;
  search?: string;
  statusFilter?: string;
}

export function useCampaignIntegrationsInfinite({
  integrationName,
  clientId,
  search,
  statusFilter,
}: UseCampaignIntegrationsInfiniteParams) {
  const normalizedName = integrationName.toLowerCase();

  return useInfiniteQuery<CampaignIntegrationsPage, AxiosError>({
    queryKey: campaignIntegrationQueryKeys.overviewInfinite({
      integrationName: normalizedName,
      clientId: clientId ?? "",
      search,
      statusFilter,
    }),
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await instance.get<{
        data: CampaignIntegrationsPage[];
      }>(`/api/v2/Integrations/${encodeURIComponent(integrationName)}/campaigns/overview`, {
        params: {
          clientId,
          search: search || undefined,
          statusFilter: statusFilter || undefined,
          page: pageParam,
          pageSize: PAGE_SIZE,
        },
      });

      const raw = response.data.data[0] ?? {
        summary: {},
        items: [],
        totalRecords: 0,
        page: pageParam as number,
        pageSize: PAGE_SIZE,
      };

      const enrichFn = enrichmentRegistry.get(normalizedName);
      return {
        ...raw,
        items: enrichFn ? enrichFn(raw.items) : raw.items,
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (acc, page) => acc + page.items.length,
        0,
      );
      return totalFetched < lastPage.totalRecords
        ? allPages.length + 1
        : undefined;
    },
    enabled: !!clientId && !!integrationName,
  });
}
